import { useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useParams } from 'react-router';
import { ActivationCheckState, ErrorCode, WEB_ROUTES } from '@lsc/shared';
import { isApiError } from '../../api/errors';
import { LoadingState } from '../../components/LoadingState';
import { checkActivationCode, publicQueryKeys } from '../api';
import { ErrorState } from '../components/ErrorState';
import { PageHeading } from '../components/PageHeading';
import { Turnstile } from '../../components/Turnstile';
import { ActivationForm } from './ActivationForm';

function needsTurnstile(error: unknown): boolean {
  return (
    isApiError(error) &&
    (error.code === ErrorCode.TURNSTILE_REQUIRED || error.code === ErrorCode.TURNSTILE_FAILED)
  );
}

/** `/a/:code` – entry from the QR code (§13): server check (§14), then the form (§50). */
export function ActivationPage() {
  const { code = '' } = useParams();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const query = useQuery({
    queryKey: publicQueryKeys.activationCheck(code, turnstileToken),
    queryFn: ({ signal }) => checkActivationCode(code, turnstileToken, signal),
  });

  if (query.isPending) return <LoadingState label="Aktivierungscode wird geprüft …" />;

  if (query.isError) {
    if (needsTurnstile(query.error)) {
      return (
        <Stack spacing={2}>
          <PageHeading>Sicherheitsprüfung</PageHeading>
          <Typography>
            Zum Schutz vor Missbrauch bestätigen Sie bitte kurz, dass Sie kein Roboter sind. Danach
            wird Ihr Code automatisch geprüft.
          </Typography>
          <Turnstile action="code-check" onToken={setTurnstileToken} />
        </Stack>
      );
    }
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  if (query.data.state === ActivationCheckState.OWN_REGISTRATION) {
    return <Navigate to={WEB_ROUTES.registrationStatus} replace />;
  }
  return <ActivationForm code={query.data.code} companyName={query.data.companyName} />;
}
